import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Heart, Sparkles, ArrowRight, Scissors, Truck, Leaf, Star } from "lucide-react";
import api from "@/lib/api";
import ProductCard from "@/components/ProductCard";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function HomePage() {
  const [products, setProducts] = useState([]);

  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
  }, []);

  return (
    <div className="grain">
      {/* HERO */}
      <section className="container-x pt-10 lg:pt-16 pb-20 lg:pb-28" data-testid="home-hero">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="inline-flex items-center gap-2 bg-[#F4EFE6] text-[#5C4D43] text-xs uppercase tracking-[0.2em] px-4 py-2 rounded-full">
              <Sparkles size={14} className="text-[#D97757]" /> a brand-new family-run shop
            </div>
            <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl tracking-tight mt-6 leading-[1.05]">
              Soft friends,<br />
              <span className="italic text-[#D97757]">stitched with</span> love.
            </h1>
            <p className="mt-6 text-lg text-[#5C4D43] max-w-lg leading-relaxed">
              Hand-crocheted animal companions — reindeers, ducks, sheep and the odd capybara —
              each one made just for you. Add a name, a birthday, a little secret message on the
              tag. They&apos;re heirlooms in the making.
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Link to="/shop" data-testid="hero-shop-btn" className="btn-primary inline-flex items-center gap-2">
                Meet the family <ArrowRight size={16} />
              </Link>
              <Link to="/personalise" data-testid="hero-custom-btn" className="btn-outline inline-flex items-center gap-2">
                <Scissors size={16} /> Request a custom friend
              </Link>
            </div>
            <div className="mt-12 flex flex-wrap items-center gap-x-8 gap-y-3 text-sm text-[#5C4D43]">
              <div className="flex items-center gap-1.5">
                <Leaf size={16} className="text-[#A3B18A]" />
                <span>Just-opened small batch studio</span>
              </div>
              <div className="flex items-center gap-1.5">
                <Heart size={16} className="fill-[#D97757] text-[#D97757]" />
                <span>Custom names & birthdays</span>
              </div>
              <div className="flex items-center gap-1.5">
                <Truck size={16} className="text-[#D97757]" />
                <span>UK delivery · Royal Mail 2nd class</span>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="relative"
          >
            <div className="absolute -inset-6 bg-[#F4EFE6] rounded-[3rem] -rotate-2"></div>
            <img
              src={`${BACKEND}/api/static/products/teddys-family-group.png`}
              alt="The Teddys Tiny Friends crochet animal family"
              className="relative rounded-[2.5rem] w-full aspect-square object-cover shadow-[0_30px_80px_rgba(44,33,27,0.10)]"
            />
            <div className="absolute -bottom-6 -left-4 bg-white rounded-2xl px-5 py-4 shadow-[0_12px_28px_rgba(44,33,27,0.08)] border border-[#EAE3D9]">
              <div className="font-script text-[#D97757] text-xl leading-none">The whole family</div>
              <div className="text-xs text-[#5C4D43] mt-1">six little friends, all made by hand</div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* SHOP / CUSTOM TILES */}
      <section className="container-x pb-4" data-testid="home-shop-tiles">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Link
            to="/shop"
            data-testid="tile-all-animals"
            className="group relative overflow-hidden rounded-3xl aspect-[16/9] bg-[#F4EFE6]"
          >
            <img
              src={`${BACKEND}/api/static/products/woolly-the-soft-sheep.png`}
              alt="All animals"
              className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/10 to-transparent" />
            <div className="absolute bottom-5 left-5 right-5">
              <div className="font-script text-xl text-white/90">browse the shop</div>
              <div className="font-serif text-3xl text-white">All animals</div>
            </div>
          </Link>

          <Link
            to="/personalise"
            data-testid="tile-custom-orders"
            className="group relative overflow-hidden rounded-3xl aspect-[16/9] bg-[#F4EFE6]"
          >
            <img
              src={`${BACKEND}/api/static/products/rudy-the-little-reindeer.png`}
              alt="Personalisation"
              className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/10 to-transparent" />
            <div className="absolute bottom-5 left-5 right-5">
              <div className="font-script text-xl text-white/90">your idea, our stitches</div>
              <div className="font-serif text-3xl text-white">Personalisation</div>
            </div>
          </Link>
        </div>
      </section>

      {/* FEATURED PRODUCTS */}
      <section className="container-x section-y" data-testid="home-featured">
        <div className="flex items-end justify-between gap-6 mb-12">
          <div>
            <div className="font-script text-2xl text-[#D97757]">just stitched</div>
            <h2 className="font-serif text-4xl lg:text-5xl tracking-tight mt-1">Newest friends</h2>
          </div>
          <Link to="/shop" data-testid="home-view-all" className="hidden md:inline-flex items-center gap-2 text-[#2C211B] hover:text-[#D97757] text-sm">
            View all <ArrowRight size={14} />
          </Link>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {products.slice(0, 6).map((p, i) => (
            <ProductCard key={p.id} product={p} index={i} />
          ))}
        </div>
      </section>

      {/* OUR STORY */}
      <section className="bg-[#F4EFE6] section-y" data-testid="home-story">
        <div className="container-x grid lg:grid-cols-2 gap-16 items-center">
          <img
            src={`${BACKEND}/api/static/products/chenille-yarn-neutral.png`}
            alt="Soft chenille yarn in neutral cream and sage tones"
            className="rounded-3xl w-full aspect-[5/4] object-cover"
          />
          <div>
            <div className="font-script text-2xl text-[#D97757]">our story</div>
            <h2 className="font-serif text-4xl lg:text-5xl mt-2 leading-tight">
              It all started with a teddy for our son, in the garden.
            </h2>
            <p className="mt-6 text-[#5C4D43] leading-relaxed">
              We stitched our very first bear for our little boy <em>Teddy</em> — handed to him on
              a warm afternoon in the garden, beneath the apple tree. He hasn&apos;t let go since.
              That single bear inspired a whole tiny menagerie, and Teddys Tiny Friends was born.
            </p>
            <div className="mt-8 grid grid-cols-3 gap-6">
              <Stat number="100%" label="Cotton yarn" />
              <Stat number="48h" label="Avg. craft time" />
              <Stat number="UK" label="Royal Mail 2nd" />
            </div>
            <Link to="/about" data-testid="story-cta" className="btn-secondary mt-8 inline-block">
              Read our story
            </Link>
          </div>
        </div>
      </section>

      {/* TRUSTPILOT */}
      <section className="container-x pb-4" data-testid="home-trustpilot">
        <a
          href="https://www.trustpilot.com/review/teddystinyfriends.co.uk"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="trustpilot-link"
          className="block card-soft p-7 lg:p-9 hover:-translate-y-1 transition-all"
        >
          <div className="grid lg:grid-cols-[1fr_auto] gap-6 items-center">
            <div>
              <div className="flex items-center gap-1.5 text-[#00B67A]">
                {["s1", "s2", "s3", "s4", "s5"].map((k) => (
                  <Star key={k} size={20} className="fill-[#00B67A]" />
                ))}
                <span className="ml-3 font-medium text-[#2C211B]">Trustpilot</span>
              </div>
              <div className="font-serif text-2xl lg:text-3xl mt-3">
                Loved a little friend? Leave us a review.
              </div>
              <p className="mt-2 text-[#5C4D43] text-sm leading-relaxed max-w-xl">
                We&apos;re a brand-new family-run shop and every review on Trustpilot
                helps another family find us. Read what others have said, or share your story.
              </p>
            </div>
            <div className="lg:justify-self-end">
              <span className="btn-primary inline-flex items-center gap-2">
                Read & write reviews <ArrowRight size={16} />
              </span>
            </div>
          </div>
        </a>
      </section>

      {/* SMALL BUSINESS BADGES */}
      <section className="container-x section-y" data-testid="home-promise">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <div className="font-script text-2xl text-[#D97757]">our little promise</div>
          <h2 className="font-serif text-4xl lg:text-5xl mt-2">Just opened. Made by hand. Stitched with care.</h2>
          <p className="mt-4 text-[#5C4D43]">
            We&apos;re a brand-new family-run shop with no minimum quantity and no factory shelves —
            every friend is crocheted just after you order, by us, at our kitchen table.
          </p>
        </div>
        <div className="grid md:grid-cols-3 gap-6 lg:gap-8" data-testid="promise-cards">
          {[
            { icon: Leaf, title: "Small batch & new", text: "A freshly-opened tiny studio. Every order is celebrated like the first." },
            { icon: Heart, title: "UK safety standards", text: "Made to the Toys (Safety) Regulations 2011 and BS EN 71. Safety eyes washer-locked & pull-tested. Adult supervision advised." },
            { icon: Truck, title: "UK delivery", text: "Posted via Royal Mail 2nd class. Timing varies with demand — we'll keep you updated." },
          ].map((c, i) => (
            <motion.div
              key={c.title}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.08 }}
              className="bg-[#F4EFE6] rounded-3xl p-8"
              data-testid={`promise-card-${i}`}
            >
              <c.icon className="text-[#D97757]" />
              <div className="font-serif text-2xl mt-4">{c.title}</div>
              <p className="mt-3 text-[#5C4D43] leading-relaxed">{c.text}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="container-x pb-24" data-testid="home-cta">
        <div className="relative overflow-hidden rounded-[2.5rem] bg-[#2C211B] text-white p-10 lg:p-16">
          <div className="absolute -top-10 -right-10 w-72 h-72 rounded-full bg-[#D97757]/20 blur-3xl" />
          <div className="absolute -bottom-10 -left-10 w-72 h-72 rounded-full bg-[#A3B18A]/20 blur-3xl" />
          <div className="relative grid lg:grid-cols-2 gap-10 items-center">
            <div>
              <div className="font-script text-2xl text-[#D8A4A4]">dreaming of a one-of-a-kind?</div>
              <h2 className="font-serif text-4xl lg:text-5xl mt-2">Design a friend just for them.</h2>
              <p className="mt-4 text-white/80 max-w-md">
                Tell us about the colors, the animal, the name, the birthday — we&apos;ll bring it to life.
              </p>
            </div>
            <div className="lg:justify-self-end">
              <Link to="/personalise" data-testid="cta-custom-btn" className="btn-primary inline-flex items-center gap-2">
                Start a personalisation request <ArrowRight size={16} />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function Stat({ number, label }) {
  return (
    <div>
      <div className="font-serif text-3xl text-[#D97757]">{number}</div>
      <div className="text-xs text-[#5C4D43] uppercase tracking-widest mt-1">{label}</div>
    </div>
  );
}
